import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationOptions,
  type UseQueryOptions,
} from '@tanstack/react-query';

import { apiGet, apiPath, apiPost } from '@/lib/api/client';
import { CubixApiError } from '@/lib/api/types';

import type { EveAnalysisDetail, TriggerEveRequest } from './types';

const eveKey = (date: string | undefined) =>
  ['alm', 'eve', 'latest', date] as const;

/**
 * GET /api/v1/alm/eve?date=YYYY-MM-DD
 *
 * Returns the persisted EVE result for the given tenant + date, or null
 * if no calculation has run for that date yet. When date is undefined the
 * server returns the most recent result.
 */
export function useLatestEve(
  date: string | undefined,
  options?: Omit<
    UseQueryOptions<EveAnalysisDetail | null, CubixApiError>,
    'queryKey' | 'queryFn'
  >,
) {
  return useQuery({
    queryKey: eveKey(date),
    enabled: Boolean(date),
    queryFn: async () => {
      const url = `${apiPath('alm', 'eve')}?date=${encodeURIComponent(date!)}`;
      const data = await apiGet<EveAnalysisDetail | null>(url);
      return data ?? null;
    },
    ...options,
  });
}

/**
 * POST /api/v1/alm/eve/calculate — triggers an on-demand EVE + KRD calculation.
 * All six Basel scenarios run in one engine call. Server returns 202 Accepted.
 */
export function useTriggerEve(
  options?: UseMutationOptions<EveAnalysisDetail, CubixApiError, TriggerEveRequest>,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body) =>
      apiPost<EveAnalysisDetail, TriggerEveRequest>(
        apiPath('alm', 'eve', 'calculate'),
        body,
      ),
    ...options,
    onSuccess: (...args) => {
      qc.invalidateQueries({ queryKey: ['alm', 'eve'] });
      return options?.onSuccess?.(...args);
    },
  });
}
